import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import Title from '../../components/common/Title';
import PersonFields from '../../components/sales/PersonFields';
import InvoiceCapture from '../../components/sales/InvoiceCapture';
import { apiRequest, apiErrorText } from '../../utils';
import './Sales.css';

/* The internal home: one screen for our own people, whatever they were granted.

   The CRM half answers "what am I standing in front of?", so the search box is the
   top of the page and results carry the two facts that decide what happens next:
   the address, and whether the company is already ours. Search is deliberately
   GLOBAL on the server (it is what stops a second copy of a company being created
   on the street), so a hit that is not in this unit is shown as exactly that, with
   the option to take it over rather than duplicate it.

   ★ The invoice capture lives HERE and not behind its own role. Whoever is holding
   a supplier invoice is very often whoever was logging a visit an hour ago, and
   making them change role to photograph a bill is precisely the friction the
   capture was built to remove. Sections are drawn per GRANT (can_sell /
   can_capture, per business unit, from /api/sales/context) rather than per role
   claim, so the operations screens can arrive the same way: another section, for
   whoever holds it, on the screen they are already on. */
const SalesHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [recent, setRecent] = useState([]);
  const [mailbox, setMailbox] = useState(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  // Every search is stamped; only the newest one is allowed to paint. Without this
  // a slow "del" can land after a fast "delhaize" and replace the right answer.
  const searchSeq = useRef(0);

  const [creating, setCreating] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', city: '' });
  const [newPerson, setNewPerson] = useState({ role: 'lead' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const buId = context?.business_unit_id;
  // Which halves of the home this person may see, for the unit they are on.
  // `!== false` rather than `=== true`: an older payload carries neither flag,
  // and defaulting to hidden would blank the screen for everybody mid-deploy.
  const canSell = context?.can_sell !== false;
  const canCapture = context?.can_capture === true;

  // Adding the person is optional here — a rep can create the company alone and
  // come back with the card. A HALF-filled person is not optional: the server
  // needs all three fields and would refuse the whole company over a missing
  // surname, losing the company too.
  const personStarted = !!(newPerson.email || newPerson.first_name || newPerson.name);
  const personReady = !!(newPerson.email && newPerson.first_name && newPerson.name);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiRequest('GET', '/sales/context');
        // ★ The server leaves business_unit_id null when the session names no unit
        // and the rep is granted several — it will not guess for them. The screen
        // must, or it renders a search box wired to nothing: every request needs a
        // bu_id, so a null one is a home that silently does nothing at all. Falling
        // back to the first (parent, then name — the order the server sorts them in)
        // gives a working screen the switcher can then correct.
        if (!data.business_unit_id && data.business_units?.length) {
          data.business_unit_id = data.business_units[0].id;
        }
        setContext(data);
      } catch {
        // No grant, or a session that is not a sales session: this screen has
        // nothing to show and saying so beats an empty page.
        setDenied(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    // Skipped entirely without a CRM grant on this unit: both calls would come
    // back 403 for somebody who only captures invoices, and two refusals per
    // page load is a poor way to ask a question we already know the answer to.
    if (!buId || !canSell) return;
    apiRequest('GET', `/sales/recent?bu_id=${buId}`)
      .then((d) => setRecent(d.companies || []))
      .catch(() => setRecent([]));
    // Asked for here rather than only in the mail sheet: a rep should find out
    // their mailbox needs reconnecting on the way to the car, not standing in
    // front of the customer with the pack ready to go.
    apiRequest('GET', `/sales/mailbox?bu_id=${buId}`)
      .then(setMailbox)
      .catch(() => setMailbox(null));
  }, [buId, canSell]);

  const runSearch = useCallback(async (q) => {
    const seq = searchSeq.current + 1;
    searchSeq.current = seq;
    if (!q || q.trim().length < 2 || !buId) {
      setResults(null);
      return;
    }
    setSearching(true);
    try {
      const data = await apiRequest(
        'GET', `/sales/search?q=${encodeURIComponent(q.trim())}&bu_id=${buId}`);
      if (searchSeq.current === seq) setResults(data);
    } catch {
      if (searchSeq.current === seq) setResults({ candidates: [] });
    } finally {
      if (searchSeq.current === seq) setSearching(false);
    }
  }, [buId]);

  // Typing on a phone on a pavement: wait for the pause, not for every letter.
  useEffect(() => {
    const id = setTimeout(() => runSearch(query), 350);
    return () => clearTimeout(id);
  }, [query, runSearch]);

  const switchBu = async (id) => {
    const unit = context?.business_units?.find((b) => b.id === id);
    // The two halves need not cover the same units, so the sections are taken
    // from the unit being switched TO rather than left showing the old one's.
    setContext((c) => ({ ...c, business_unit_id: id,
                         can_sell: unit ? unit.can_sell : c.can_sell,
                         can_capture: unit ? unit.can_capture : c.can_capture }));
    setResults(null);
    try {
      // Re-mint the session so a reload keeps the unit just picked. The descriptor
      // key is 'internal' — one card per unit, whatever you were granted on it —
      // and the role claim follows what this person actually holds there. The
      // NavBar caches the role in sessionStorage, so keep that in step too or the
      // bottom bar drifts back to the previous role's icons.
      const role = unit && unit.can_sell === false ? 'finance' : 'sales';
      await apiRequest('POST', '/role', { key: 'internal', role, bu_id: id });
      sessionStorage.setItem('role', role);
    } catch { /* the in-memory switch above still works for this session */ }
  };

  const createCompany = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { bu_id: buId, name: newCompany.name, city: newCompany.city };
      // The person is nested: a company and a person both have a `name`, and one
      // flat body would give the new contact the company's name as their surname.
      if (personReady) payload.person = newPerson;
      const data = await apiRequest('POST', '/sales/company', payload);
      navigate(`/sales/company/${data.company_id}`);
    } catch (err) {
      setError(apiErrorText(t, err, 'somethingWentWrong'));
      setSaving(false);
    }
  };

  const adopt = async (companyId) => {
    try {
      await apiRequest('POST', `/sales/company/${companyId}/adopt`, { bu_id: buId });
      navigate(`/sales/company/${companyId}`);
    } catch (err) {
      setError(apiErrorText(t, err, 'somethingWentWrong'));
    }
  };

  if (loading) return <Base activeNav="home"><Loading /></Base>;

  if (denied) {
    return (
      <Base activeNav="home">
        <Title>{t('salesHome')}</Title>
        <Card><p className="sales-hint">{t('salesNoAccess')}</p></Card>
      </Base>
    );
  }

  return (
    <Base activeNav="home">
      <div className="sales-head">
        {/* "On the road" is the rep's word for this screen and stays the title
            for anyone who sells. Somebody granted only the capture is not on any
            road, so they get the name of what they actually see. */}
        <Title my="3">{t(canSell ? 'salesHome' : 'financeHome')}</Title>
        {context?.business_units?.length > 1 && (
          <select
            className="sales-input sales-bu"
            value={buId || ''}
            onChange={(e) => switchBu(e.target.value)}
          >
            {context.business_units.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Drawn per GRANT, not per role claim: somebody granted only the invoice
          capture on this unit must not be shown a search box wired to a CRM
          they cannot read. `can_sell` is undefined on an older payload, so the
          fallback keeps the CRM visible rather than blanking the screen. */}
      {canSell && (
      <Card>
        <div className="sales-search">
          <Icon name="search" />
          <input
            className="sales-input"
            value={query}
            placeholder={t('salesSearchCompany')}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          {searching && <Loading size="sm" showLabel={false} inline />}
        </div>

        {results && (
          <div className="sales-results">
            {results.candidates?.length ? results.candidates.map((c) => (
              <div key={c.id} className="sales-result">
                <div className="sales-result__main"
                     onClick={() => c.in_bu && navigate(`/sales/company/${c.id}`)}>
                  <span className="sales-result__name">{c.name}</span>
                  {c.city && <span className="sales-result__sub">{c.city}</span>}
                </div>
                {c.in_bu
                  ? <Icon name="angle-right" />
                  : (
                    <button type="button" className="sales-adopt" onClick={() => adopt(c.id)}>
                      {t('salesAddToPipeline')}
                    </button>
                  )}
              </div>
            )) : (
              <p className="sales-hint">{t('salesNoResults', { query })}</p>
            )}
            <Button theme="secondary" mb="0" onClick={() => {
              setCreating(true);
              setNewCompany({ name: query, city: '' });
            }}>
              <Icon name="plus" /> {t('salesCreateCompany')}
            </Button>
          </div>
        )}
      </Card>
      )}

      {/* ★ The invoices we received are NOT on this screen for anyone who sells.
          On the road the first focus is sales: a rep opens this several times a
          day to look a company up before walking in, and an invoice arrives now
          and then. Two jobs competing for the top of one screen means the
          frequent one loses, so the capture moved to a page of its own and this
          screen carries one discreet button to it (rendered at the bottom).

          Somebody granted ONLY the capture keeps it right here: for them it is
          not a detour off their work, it IS their work. */}
      {canCapture && !canSell && <InvoiceCapture buId={buId} />}

      {creating && (
        <Card>
          <h6>{t('salesNewCompany')}</h6>
          <input className="sales-input" placeholder={t('salesCompanyName')}
                 value={newCompany.name}
                 onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })} />
          <input className="sales-input" placeholder={t('city')}
                 value={newCompany.city}
                 onChange={(e) => setNewCompany({ ...newCompany, city: e.target.value })} />
          <p className="sales-hint">{t('salesNewCompanyHint')}</p>
          <PersonFields value={newPerson} onChange={setNewPerson} buId={buId} />
          {error && <p className="sales-error">{error}</p>}
          <div className="sales-actions">
            <Button theme="secondary" mb="0" styles={{ width: '40%' }}
                    onClick={() => { setCreating(false); setError(null); }}>
              {t('cancel')}
            </Button>
            {personStarted && !personReady && (
              <p className="sales-hint">{t('salesPersonNeedsAllThree')}</p>
            )}
            <Button mb="0" styles={{ width: '58%' }}
                    disabled={saving || !newCompany.name || (personStarted && !personReady)}
                    onClick={createCompany}>
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        </Card>
      )}

      {mailbox && !mailbox.connected && (
        <Card>
          <h6>{t('salesMailbox')}</h6>
          <p className="sales-hint">
            {mailbox.status === 'reauth_required'
              ? t('salesMailboxReconnect')
              : t('salesMailboxNotConnected')}
          </p>
          <Button mb="0" onClick={() => {
            window.location.href = `/api/sales/mailbox/start?bu_id=${buId}`;
          }}>
            <Icon name="envelope" /> {t('salesConnectMailbox')}
          </Button>
        </Card>
      )}

      {!!recent.length && !results && (
        <Card>
          <h6>{t('salesRecentlyVisited')}</h6>
          {recent.map((c) => (
            <div key={c.id} className="sales-result"
                 onClick={() => navigate(`/sales/company/${c.id}`)}>
              <div className="sales-result__main">
                <span className="sales-result__name">{c.name}</span>
                {c.address && <span className="sales-result__sub">{c.address}</span>}
              </div>
              <Icon name="angle-right" />
            </div>
          ))}
        </Card>
      )}

      {/* The other job, kept deliberately quiet and last: one line, no camera, no
          list of unpaid bills competing with the search box above it. */}
      {canSell && canCapture && (
        <button type="button" className="sales-mini sales-mini--wide"
                onClick={() => navigate('/finance-home')}>
          <Icon name="camera" /> {t('financePhotographInvoice')}
        </button>
      )}
    </Base>
  );
};

export default SalesHome;
